import { useState, useCallback } from "react";
import { AlertTriangle } from "lucide-react";

/**
 * useConfirm
 * Drop-in replacement for window.confirm().
 * Usage:
 *   const { confirm, confirmState, handleConfirm, handleCancel } = useConfirm();
 *   const ok = await confirm("Are you sure you want to delete this?");
 *   if (!ok) return;
 *
 * Render <ConfirmDialog confirmState={confirmState} onConfirm={handleConfirm} onCancel={handleCancel} />
 * once, near the top of the component's returned JSX.
 */
export function useConfirm() {
  const [confirmState, setConfirmState] = useState(null); // { message, resolve }

  const confirm = useCallback((message) => {
    return new Promise((resolve) => {
      setConfirmState({ message, resolve });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setConfirmState((current) => {
      current?.resolve(true);
      return null;
    });
  }, []);

  const handleCancel = useCallback(() => {
    setConfirmState((current) => {
      current?.resolve(false);
      return null;
    });
  }, []);

  return { confirm, confirmState, handleConfirm, handleCancel };
}

/**
 * ConfirmDialog - centered modal confirmation prompt.
 * Render this once per page, alongside the useConfirm() hook.
 */
export default function ConfirmDialog({ confirmState, onConfirm, onCancel }) {
  if (!confirmState) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/40 px-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-5">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center">
            <AlertTriangle size={18} className="text-amber-500" />
          </div>
          <p className="text-sm font-bold text-slate-700 leading-snug pt-1.5">
            {confirmState.message}
          </p>
        </div>
        <div className="mt-5 flex justify-end gap-2.5">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-bold border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg text-sm font-bold bg-red-600 text-white hover:bg-red-700 transition"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}